import { onUnmounted } from 'vue';

export interface DebouncedSearch {
    onKeyUp: (event: any) => void,
    flush: () => void,
    cancel: () => void,
}

/**
 * A type-ahead search whose keystrokes are debounced before they reach the network.
 *
 * This replaces the `if (!store.loading)` guard the finders previously used. That guard was
 * not a debounce: it DROPPED any keystroke that arrived while a request was in flight and
 * never retried, so the rendered list reflected whichever keystroke won the race rather than
 * what the user had typed. Backspacing to a shorter string was the common casualty — the
 * shorter query landed mid-request, was discarded, and the stale longer-string results stayed
 * on screen. It also read the GLOBAL `appData.loading`, so an unrelated request anywhere in
 * the app could suppress a search.
 *
 * Debouncing fixes it at the source: every keystroke re-arms the timer, and the value the
 * user settles on always reaches the network exactly once. No global state is consulted, so
 * concurrent requests elsewhere cannot suppress a query.
 *
 * The timer and its cleanup live here rather than in each of the eight finders, for the same
 * reason as `useDebouncedRange`: a hand-rolled timer per component is where a missed
 * clearTimeout lives.
 */
export function useDebouncedSearch(
    onSettled: (value: string) => void,
    ms: number = 300,
): DebouncedSearch {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending = '';

    const cancel = (): void => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };

    const onKeyUp = (event: any): void => {
        pending = event?.target?.value ?? '';
        cancel();
        timer = setTimeout(() => {
            timer = null;
            onSettled(pending);
        }, ms);
    };

    /**
     * Fire now, cancelling any armed timer — for a caller that needs the settled value
     * immediately (an Enter key, a browse button) without waiting out the delay, and without
     * the armed timer then issuing a second identical request.
     */
    const flush = (): void => {
        cancel();
        onSettled(pending);
    };

    onUnmounted(cancel);

    return { onKeyUp, flush, cancel };
}
